import { useTheme } from "../ThemeContext";
import { useState, useEffect, useRef, useCallback } from "react";
import { fmt, STATUS, DONE_STATUS } from "../theme";
import T from "../i18n";
import { post, get, put } from "../api";

// ─────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────
const DIAS_SEMANA = ["dom","seg","ter","qua","qui","sex","sab"];

function isLojaOnlineAgora(loja) {
  if (!loja) return false;
  const agora = new Date();
  const diaSemana = DIAS_SEMANA[agora.getDay()];
  const dias = (loja.dias_funcionamento || "seg,ter,qua,qui,sex,sab,dom").split(",").map(d => d.trim());
  if (!dias.includes(diaSemana)) return false;
  const horaAtual = agora.getHours() * 60 + agora.getMinutes();
  const [ah, am] = (loja.horario_abre || "08:00").split(":").map(Number);
  const [fh, fm] = (loja.horario_fecha || "23:00").split(":").map(Number);
  return horaAtual >= ah * 60 + am && horaAtual < fh * 60 + fm;
}

function minutosDesde(ts) {
  if (!ts) return 0;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
}
function timerCor(min) {
  if (min >= 6) return "#E8201A";
  if (min >= 3) return "#FACC15";
  return "#22C55E";
}
function timerPct(min, max = 10) {
  return Math.min(100, (min / max) * 100);
}

const PROGRESS_STEPS = [
  { key: "novo",                label: "Novo",        icon: "🔔" },
  { key: "aceito",              label: "Aceito",      icon: "✓" },
  { key: "buscando_entregador", label: "Entregador",  icon: "📦" },
  { key: "em_entrega",          label: "Em rota",     icon: "🛵" },
  { key: "entregue",            label: "Entregue",    icon: "✅" },
];
function progressIndex(status) {
  if (status === "pronto") return 2; // pronto = mesma fase que buscando_entregador
  if (status === "preparando") return 1; // preparando = mesma fase que aceito
  const idx = PROGRESS_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
}

export default function Pedidos({ orders, onAdvance, onReject, onRefresh, lang, lojaId: _lojaIdProp }) {
  const lojaId = _lojaIdProp || (() => {
    try {
      for (const k of ["mixup_loja","loja","user","lojista","auth","session"]) {
        const raw = localStorage.getItem(k); if (!raw) continue;
        const u = JSON.parse(raw);
        const id = u?.loja_id || u?.id || u?.loja?.id;
        if (id) return id;
      }
    } catch {} return null;
  })();
  const { theme: C } = useTheme();
  const _ = (k) => T(lang, k);

  // ── estados ──
  const [selected, setSelected]         = useState(null);
  const [toast, setToast]               = useState(null);
  const [busca, setBusca]               = useState("");

  // ── loja / online ──
  const [loja, setLoja]                 = useState(null);
  const [lojaOnline, setLojaOnline]     = useState(false);
  const [togglingOnline, setTogglingOnline] = useState(false);

  // ── saldo ──
  const [saldo, setSaldo]               = useState(null);
  const [showSaldo, setShowSaldo]       = useState(false);

  // ── vendas ──
  const [showVendas, setShowVendas]     = useState(false);

  // ── Som ──
  const somRef = useRef(null);
  const somIntervalRef = useRef(null);
  const pararSom = () => {
    if (somIntervalRef.current) { clearInterval(somIntervalRef.current); somIntervalRef.current = null; }
    if (somRef.current) { somRef.current.pause(); somRef.current.currentTime = 0; }
  };
  const tocarSom = () => {
    if (!somRef.current) return;
    somRef.current.currentTime = 0;
    somRef.current.play().catch(() => {});
    if (somIntervalRef.current) clearInterval(somIntervalRef.current);
    somIntervalRef.current = setInterval(() => {
      if (somRef.current) { somRef.current.currentTime = 0; somRef.current.play().catch(() => {}); }
    }, 4000);
  };

  // ── auto-aceite ──
  const [autoAceite, setAutoAceite] = useState(() => localStorage.getItem("mixup_auto_aceite") === "1");

  // ── Chat ──
  const [showChat, setShowChat]         = useState(false);
  const [chatPedido, setChatPedido]     = useState(null);
  const [showChatEnt, setShowChatEnt]   = useState(false);
  const [chatEntPedido, setChatEntPedido] = useState(null);
  const [chatEntConversa, setChatEntConversa] = useState(null);
  const [chatEntMsgs, setChatEntMsgs]   = useState([]);
  const [chatEntTexto, setChatEntTexto] = useState("");
  const [chatEntLoading, setChatEntLoading] = useState(false);
  const [chatConversa, setChatConversa] = useState(null);
  const [chatMsgs, setChatMsgs]         = useState([]);
  const [chatTexto, setChatTexto]       = useState("");
  const [chatLoading, setChatLoading]   = useState(false);
  const [chatNaoLidos, setChatNaoLidos] = useState({});
  const [chatNaoLidosEnt, setChatNaoLidosEnt] = useState({});
  const chatScrollRef                   = useRef(null);
  const chatEntScrollRef                = useRef(null);
  const chatEntConversaRef              = useRef(null);
  const chatEntUltimaRef                = useRef(null);
  const chatEntPollRef                  = useRef(null);
  const chatUltimaRef                   = useRef(null);
  const chatConversaRef                 = useRef(null);
  const chatPollRef                     = useRef(null);

  // Detectar novos pedidos
  const prevOrdersRef = useRef([]);
  useEffect(() => {
    const prevIds = new Set(prevOrdersRef.current.map(o => o.id));
    const novos = orders.filter(o => o.status === "novo" && !prevIds.has(o.id));
    if (novos.length > 0) {
      tocarSom();
      if (autoAceite) {
        novos.forEach(o => { setTimeout(() => { onAdvance(o.id); imprimirPedido(o); }, 800); });
      }
    }
    if (!orders.some(o => o.status === "novo")) pararSom();
    prevOrdersRef.current = orders;
  }, [orders]);

  const toggleAutoAceite = () => {
    const novo = !autoAceite;
    setAutoAceite(novo);
    localStorage.setItem("mixup_auto_aceite", novo ? "1" : "0");
    notify(novo ? "Auto-aceite ativado" : "Auto-aceite desativado", novo);
  };

  // ── Chat functions ──
  const abrirChat = async (pedido) => {
    markInteracting(); setChatPedido(pedido); setChatMsgs([]);
    chatUltimaRef.current = null; chatConversaRef.current = null;
    setShowChat(true); setChatLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=conversa`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participante_a_tipo:"loja", participante_a_id:lojaId, participante_b_tipo:"cliente", participante_b_id:pedido.cliente_id, pedido_id:pedido.id }),
      });
      const d = await r.json();
      if (d.ok) {
        setChatConversa(d.conversa); chatConversaRef.current = d.conversa;
        const r2 = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${d.conversa.id}&marcar_lido=loja:${lojaId}`);
        const d2 = await r2.json();
        if (d2.ok) { setChatMsgs(d2.mensagens || []); if (d2.mensagens?.length) chatUltimaRef.current = d2.mensagens[d2.mensagens.length-1].criado_em; }
      }
    } catch {} setChatLoading(false);
    clearInterval(chatPollRef.current);
    chatPollRef.current = setInterval(async () => {
      if (!chatConversaRef.current?.id) return;
      try {
        const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${chatConversaRef.current.id}&marcar_lido=loja:${lojaId}${chatUltimaRef.current ? "&desde="+encodeURIComponent(chatUltimaRef.current) : ""}`);
        const d = await r.json();
        if (d.ok && d.mensagens?.length > 0) {
          setChatMsgs(prev => { const ids = new Set(prev.map(m=>String(m.id))); const n = d.mensagens.filter(m=>!ids.has(String(m.id))); return n.length ? [...prev,...n] : prev; });
          chatUltimaRef.current = d.mensagens[d.mensagens.length-1].criado_em;
          setTimeout(() => chatScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
        }
      } catch {}
    }, 3000);
  };
  const fecharChat = () => { setShowChat(false); clearInterval(chatPollRef.current); chatConversaRef.current = null; chatUltimaRef.current = null; };

  const abrirChatEnt = async (pedido) => {
    setChatEntPedido(pedido); setChatEntMsgs([]); chatEntConversaRef.current = null; chatEntUltimaRef.current = null;
    setShowChatEnt(true); setChatEntLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=conversa`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participante_a_tipo:"loja", participante_a_id:lojaId, participante_b_tipo:"entregador", participante_b_id:pedido.entregador_id, pedido_id:pedido.id }),
      });
      const d = await r.json();
      if (d.ok) {
        setChatEntConversa(d.conversa); chatEntConversaRef.current = d.conversa;
        const r2 = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${d.conversa.id}&marcar_lido=loja:${lojaId}`);
        const d2 = await r2.json();
        if (d2.ok) { setChatEntMsgs(d2.mensagens || []); if (d2.mensagens?.length) chatEntUltimaRef.current = d2.mensagens[d2.mensagens.length-1].criado_em; }
      }
    } catch {} setChatEntLoading(false);
    clearInterval(chatEntPollRef.current);
    chatEntPollRef.current = setInterval(async () => {
      if (!chatEntConversaRef.current?.id) return;
      try {
        const desde = chatEntUltimaRef.current ? "&desde="+encodeURIComponent(chatEntUltimaRef.current) : "";
        const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${chatEntConversaRef.current.id}&marcar_lido=loja:${lojaId}${desde}`);
        const d = await r.json();
        if (d.ok && d.mensagens?.length > 0) {
          setChatEntMsgs(prev => { const ids = new Set(prev.map(m=>String(m.id))); const n = d.mensagens.filter(m=>!ids.has(String(m.id))); return n.length ? [...prev,...n] : prev; });
          chatEntUltimaRef.current = d.mensagens[d.mensagens.length-1].criado_em;
          setTimeout(() => chatEntScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
        }
      } catch {}
    }, 3000);
  };
  const fecharChatEnt = () => { setShowChatEnt(false); clearInterval(chatEntPollRef.current); chatEntConversaRef.current = null; chatEntUltimaRef.current = null; };

  const enviarMsgChat = async () => {
    const txt = chatTexto.trim(); if (!txt || !chatConversaRef.current) return;
    const tempId = "tmp_"+Date.now(); setChatTexto("");
    setChatMsgs(prev => [...prev, { id:tempId, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt, criado_em:new Date().toISOString(), lido:0 }]);
    setTimeout(() => chatScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
    try {
      const res = await fetch(`https://mixupbr.com.br/api/chat/?acao=enviar`, { method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ conversa_id:chatConversaRef.current.id, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt }) });
      const d = await res.json();
      if (d.ok && d.mensagem) { setChatMsgs(prev => prev.map(m => m.id===tempId ? d.mensagem : m)); chatUltimaRef.current = d.mensagem.criado_em; }
    } catch {}
  };
  const enviarMsgChatEnt = async () => {
    const txt = chatEntTexto.trim(); if (!txt || !chatEntConversaRef.current) return;
    const tempId = "tmp_"+Date.now(); setChatEntTexto("");
    setChatEntMsgs(prev => [...prev, { id:tempId, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt, criado_em:new Date().toISOString(), lido:0 }]);
    setTimeout(() => chatEntScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
    try {
      const res = await fetch(`https://mixupbr.com.br/api/chat/?acao=enviar`, { method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ conversa_id:chatEntConversaRef.current.id, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt }) });
      const d = await res.json();
      if (d.ok && d.mensagem) { setChatEntMsgs(prev => prev.map(m => m.id===tempId ? d.mensagem : m)); chatEntUltimaRef.current = d.mensagem.criado_em; }
    } catch {}
  };

  // Polling não lidos
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await fetch(`https://mixupbr.com.br/api/chat/?tipo=loja&id=${lojaId}`);
        const d = await r.json();
        if (d.ok) {
          const mapCli = {}, mapEnt = {};
          (d.conversas||[]).forEach(c => {
            if (c.pedido_id && c.nao_lidos > 0) {
              const outro = c.participante_a_tipo === "loja" ? c.participante_b_tipo : c.participante_a_tipo;
              if (outro === "entregador") mapEnt[c.pedido_id] = (mapEnt[c.pedido_id]||0) + parseInt(c.nao_lidos);
              else mapCli[c.pedido_id] = (mapCli[c.pedido_id]||0) + parseInt(c.nao_lidos);
            }
          });
          setChatNaoLidos(mapCli); setChatNaoLidosEnt(mapEnt);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, [lojaId]);

  // ── Impressão ──
  const imprimirPedido = (pedido) => {
    const p = pedido || sel; if (!p) return;
    const end = p.endereco_entrega || "—";
    const itensHtml = (p.itens||[]).map(it => `<tr><td>${it.quantidade}x ${it.nome}</td><td style="text-align:right">${fmt(it.preco_total||0)}</td></tr>`).join("");
    const obs = p.observacao?.includes("[ENTREGA AVULSA]") ? p.observacao.replace("[ENTREGA AVULSA] ","") : (p.observacao || "");
    const w = window.open("","_blank","width=380,height=600");
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pedido #${p.codigo}</title>
      <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;font-size:13px;padding:16px;color:#000;background:#fff}
      h2{text-align:center;font-size:18px;margin-bottom:4px}.sub{text-align:center;font-size:11px;color:#555;margin-bottom:12px}
      hr{border:none;border-top:1px dashed #000;margin:10px 0}table{width:100%;border-collapse:collapse}td{padding:3px 0;vertical-align:top}
      .total{font-size:16px;font-weight:bold}.label{font-size:11px;color:#555;margin-top:6px}.val{font-size:13px;font-weight:bold}@media print{body{padding:8px}}</style>
    </head><body><h2>MixUp</h2><div class="sub">${new Date().toLocaleString("pt-BR")}</div><hr>
      <div class="label">PEDIDO</div><div class="val" style="font-size:16px">#${p.codigo}</div><hr>
      <div class="label">CLIENTE</div><div class="val">${obs.match(/Cliente: ([^•]+)/)?.[1]?.trim() || `#${p.cliente_id}`}</div>
      <div class="label">TELEFONE</div><div class="val">${obs.match(/Tel: ([^•]+)/)?.[1]?.trim() || "—"}</div>
      <div class="label">ENDEREÇO</div><div class="val">${end}</div>
      ${obs && !obs.includes("Cliente:") ? `<div class="label">OBS</div><div class="val">${obs}</div>` : ""}
      <hr><div class="label" style="margin-bottom:6px">ITENS</div><table>${itensHtml}</table><hr>
      <table><tr><td>Subtotal</td><td style="text-align:right">${fmt(p.subtotal)}</td></tr>
      <tr><td>Entrega</td><td style="text-align:right">${fmt(p.taxa_entrega)}</td></tr>
      <tr class="total"><td><b>TOTAL</b></td><td style="text-align:right"><b>${fmt(p.total)}</b></td></tr></table><hr>
      <div class="label">PAGAMENTO</div><div class="val">${(p.forma_pagamento||"—").toUpperCase()}</div></body></html>`);
    w.document.close(); setTimeout(() => { w.print(); w.close(); }, 400);
  };

  // ── Entrega avulsa ──
  const [showAvulsa, setShowAvulsa] = useState(false);
  const [avulsaStep, setAvulsaStep] = useState(1);
  const [avulsaBusca, setAvulsaBusca] = useState("");
  const [avulsaResultados, setAvulsaResultados] = useState([]);
  const [avulsaCliente, setAvulsaCliente] = useState(null);
  const [avulsaForm, setAvulsaForm] = useState({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"" });
  const [avulsaLoading, setAvulsaLoading] = useState(false);
  const [wpTexto, setWpTexto] = useState("");
  const [wpParsing, setWpParsing] = useState(false);
  const [avulsaTaxa, setAvulsaTaxa] = useState(null);
  const [avulsaTaxaInfo, setAvulsaTaxaInfo] = useState("");
  const [avulsaCalcTaxa, setAvulsaCalcTaxa] = useState(false);
  const [avulsaFormaPag, setAvulsaFormaPag] = useState("dinheiro");
  const [avulsaJaPagou, setAvulsaJaPagou] = useState(false);
  const [avulsaTroco, setAvulsaTroco] = useState("");
  const [lojaModo, setLojaModo] = useState(null); // { tipo:'km'|'bairro'|'fixo', bairros:[], valor_fixo:0 }
  const [lojaModoLoading, setLojaModoLoading] = useState(false);

  // ── Localização ──
  const [avulsaLat, setAvulsaLat] = useState(null);
  const [avulsaLng, setAvulsaLng] = useState(null);
  const [avulsaEndGeo, setAvulsaEndGeo] = useState("");
  const [locCode, setLocCode] = useState(null);
  const [locLink, setLocLink] = useState("");
  const [locMsgWp, setLocMsgWp] = useState("");
  const [locPolling, setLocPolling] = useState(false);
  const [locStatus, setLocStatus] = useState(""); // "" | "pendente" | "recebido"
  const [geoLoading, setGeoLoading] = useState(false);
  const locPollRef = useRef(null);

  const resetLocalizacao = () => {
    setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo(""); setLocCode(null); setLocLink(""); setLocMsgWp(""); setLocPolling(false); setLocStatus("");
    if (locPollRef.current) { clearInterval(locPollRef.current); locPollRef.current = null; }
  };

  // Criar link de localização
  const criarLinkLoc = async () => {
    try {
      const r = await fetch("https://mixupbr.com.br/loc/?acao=criar", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loja_id: lojaId || 1, nome: avulsaForm.nome, telefone: avulsaForm.telefone })
      });
      const d = await r.json();
      if (d.ok) {
        setLocCode(d.code); setLocLink(d.link); setLocMsgWp(d.mensagem_whatsapp); setLocStatus("pendente"); setLocPolling(true);
        // Copiar mensagem pro clipboard
        try { await navigator.clipboard.writeText(d.mensagem_whatsapp); notify("Link copiado! Cole no WhatsApp"); } catch { notify("Link gerado! Copie manualmente"); }
        // Iniciar polling
        if (locPollRef.current) clearInterval(locPollRef.current);
        locPollRef.current = setInterval(async () => {
          try {
            const r2 = await fetch(`https://mixupbr.com.br/loc/?acao=checar&code=${d.code}`);
            const d2 = await r2.json();
            if (d2.ok && d2.status === "recebido") {
              setAvulsaLat(d2.latitude); setAvulsaLng(d2.longitude);
              if (d2.endereco_reverso) { setAvulsaEndGeo(d2.endereco_reverso); setAvulsaForm(f => ({ ...f, endereco: d2.endereco_reverso })); }
              setLocStatus("recebido"); setLocPolling(false);
              clearInterval(locPollRef.current); locPollRef.current = null;
              notify("Localização recebida do cliente!");
            }
          } catch {}
        }, 3000);
      }
    } catch { notify("Erro ao gerar link", false); }
  };

  // Geocoding do endereço
  const geocodarEndereco = async () => {
    const end = avulsaForm.endereco || avulsaForm.bairro;
    if (!end) { notify("Preencha o endereço primeiro", false); return; }
    setGeoLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/loc/?acao=geocode&endereco=${encodeURIComponent(end)}`);
      const d = await r.json();
      if (d.ok) {
        setAvulsaLat(d.latitude); setAvulsaLng(d.longitude); setAvulsaEndGeo(d.endereco_formatado);
        if (d.endereco_formatado) setAvulsaForm(f => ({ ...f, endereco: d.endereco_formatado }));
        notify("Endereço localizado no mapa!");
      } else { notify(d.erro || "Endereço não encontrado", false); }
    } catch { notify("Erro no geocoding", false); }
    setGeoLoading(false);
  };

  // Detectar link Google Maps no texto — ordem de prioridade (mais preciso primeiro)
  const extrairCoordsDeTexto = (texto) => {
    // 1. !3d e !4d no data do Google Maps (PIN EXATO — mais preciso)
    //    Ex: ...data=!4m4!3m3!8m2!3d-22.5289444!4d-55.7171389
    const m3d = texto.match(/!3d(-?[\d.]+).*?!4d(-?[\d.]+)/);
    if (m3d) return { lat: parseFloat(m3d[1]), lng: parseFloat(m3d[2]) };

    // 2. ?q=lat,lng ou &q=lat,lng (link de compartilhamento direto)
    //    Ex: maps.google.com/?q=-22.53,-55.72
    const mQ = texto.match(/[?&]q=(-?[\d.]+)[,%20]+([-]?[\d.]+)/);
    if (mQ) return { lat: parseFloat(mQ[1]), lng: parseFloat(mQ[2]) };

    // 3. /place/.../@lat,lng (posição da câmera — menos preciso que !3d!4d)
    //    Ex: maps/place/.../@-22.5287394,-55.7189441,17z
    const mAt = texto.match(/@(-?[\d.]+),(-?[\d.]+)/);
    if (mAt) return { lat: parseFloat(mAt[1]), lng: parseFloat(mAt[2]) };

    // 4. Coordenadas DMS (graus, minutos, segundos) — Ex: 22°31'44.2"S 55°43'01.7"W
    const dmsPattern = /(\d{1,3})[°º](\d{1,2})[''′](\d{1,2}(?:\.\d+)?)[""″]?\s*([NSns])\s*(\d{1,3})[°º](\d{1,2})[''′](\d{1,2}(?:\.\d+)?)[""″]?\s*([EWewOo])/;
    const mDms = texto.match(dmsPattern);
    if (mDms) {
      let lat = parseFloat(mDms[1]) + parseFloat(mDms[2])/60 + parseFloat(mDms[3])/3600;
      let lng = parseFloat(mDms[5]) + parseFloat(mDms[6])/60 + parseFloat(mDms[7])/3600;
      if (mDms[4].toUpperCase() === 'S') lat = -lat;
      if (mDms[8].toUpperCase() === 'W' || mDms[8].toUpperCase() === 'O') lng = -lng;
      return { lat: parseFloat(lat.toFixed(7)), lng: parseFloat(lng.toFixed(7)) };
    }

    // 5. DMS URL-encoded — Ex: 22%C2%B031'44.2%22S+55%C2%B043'01.7%22W
    try {
      const decoded = decodeURIComponent(texto);
      const mDmsDec = decoded.match(dmsPattern);
      if (mDmsDec) {
        let lat = parseFloat(mDmsDec[1]) + parseFloat(mDmsDec[2])/60 + parseFloat(mDmsDec[3])/3600;
        let lng = parseFloat(mDmsDec[5]) + parseFloat(mDmsDec[6])/60 + parseFloat(mDmsDec[7])/3600;
        if (mDmsDec[4].toUpperCase() === 'S') lat = -lat;
        if (mDmsDec[8].toUpperCase() === 'W' || mDmsDec[8].toUpperCase() === 'O') lng = -lng;
        return { lat: parseFloat(lat.toFixed(7)), lng: parseFloat(lng.toFixed(7)) };
      }
    } catch {}

    // 6. Coordenadas soltas no texto — Ex: -22.5289444, -55.7171389
    const mSolto = texto.match(/(-?\d{1,3}\.\d{3,8})\s*[,;]\s*(-?\d{1,3}\.\d{3,8})/);
    if (mSolto) {
      const lat = parseFloat(mSolto[1]), lng = parseFloat(mSolto[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }

    return null;
  };

  const refreshTimerRef = useRef(null);
  const userInteracting = useRef(false);
  const interactTimer = useRef(null);

  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 2800); };

  useEffect(() => {
    const lId = lojaId || 1;
    const aplicarLoja = (l) => {
      if (!l?.id) return; setLoja(l);
      if (l.online_manual !== null && l.online_manual !== undefined && l.online_manual !== "") setLojaOnline(Number(l.online_manual) === 1);
      else setLojaOnline(isLojaOnlineAgora(l));
      if (l.saldo_credito !== undefined) setSaldo(l.saldo_credito);
    };
    get("loja_config", { loja_id:lId, acao:"dados" })
      .then(r => { if (r.dados?.id) aplicarLoja(r.dados); else throw new Error(); })
      .catch(() => {
        get("lojas", { id:lId }).then(r => { const l = Array.isArray(r.dados) ? r.dados[0] : r.dados || r; aplicarLoja(l); })
        .catch(() => setLojaOnline(isLojaOnlineAgora({ horario_abre:"08:00", horario_fecha:"23:00", dias_funcionamento:"seg,ter,qua,qui,sex,sab,dom" })));
      });
  }, [lojaId]);

  const triggerRefresh = useCallback(() => { if (!userInteracting.current) onRefresh(); }, [onRefresh]);
  useEffect(() => { refreshTimerRef.current = setInterval(triggerRefresh, 20000); return () => clearInterval(refreshTimerRef.current); }, [triggerRefresh]);

  const markInteracting = () => {
    userInteracting.current = true;
    clearTimeout(interactTimer.current);
    interactTimer.current = setTimeout(() => { userInteracting.current = false; }, 5000);
  };

  const toggleOnline = async () => {
    const novoStatus = !lojaOnline; setLojaOnline(novoStatus);
    notify(novoStatus ? "Loja ONLINE ✓" : "Loja OFFLINE", novoStatus);
    setTogglingOnline(true);
    try { await post("loja_config", { loja_id: loja?.id || lojaId || 1, acao: "toggle_online", online: novoStatus }); } catch {}
    setTogglingOnline(false);
  };

  const criarPedidoTeste = async () => {
    markInteracting();
    try {
      const codigo = "TST" + String(Math.floor(Math.random() * 9000) + 1000);
      const res = await post("pedidos", { codigo, cliente_id:1, loja_id:lojaId||1, status:"novo", subtotal:"29.90", taxa_entrega:"5.00",
        desconto:"0", total:"34.90", forma_pagamento:"pix", endereco_entrega:"Rua Teste, 123 - Ponta Porã",
        observacao:"PEDIDO DE TESTE — pode excluir",
        itens: [{ produto_id:1, nome:"X-Bacon Duplo (TESTE)", quantidade:2, preco_unitario:"14.95", preco_total:"29.90" }] });
      if (res.id || res.success) { notify(`Pedido teste #${codigo} criado!`); onRefresh(); }
      else notify(res.error || "Erro ao criar pedido teste", false);
    } catch (e) { notify("Erro: " + e.message, false); }
  };

  const buscarCliente = async () => {
    if (!avulsaBusca.trim()) return; setAvulsaLoading(true);
    try { const r = await get("loja_config", { acao:"buscar_cliente", busca:avulsaBusca, loja_id:lojaId||1 }); setAvulsaResultados(r.dados || []); }
    catch { setAvulsaResultados([]); } setAvulsaLoading(false);
  };
  const selecionarClienteAvulsa = (cli) => {
    setAvulsaCliente(cli);
    setAvulsaForm({ nome:cli.nome||"", telefone:cli.telefone||"", endereco:cli.endereco||cli.endereco_padrao||"", complemento:cli.complemento||"", bairro:cli.bairro||"", cidade:cli.cidade||"Ponta Porã", valor:"", obs:cli.obs_entrega||"" });
    // Carregar lat/lng se o cliente já tem
    if (cli.latitude && cli.longitude) { setAvulsaLat(parseFloat(cli.latitude)); setAvulsaLng(parseFloat(cli.longitude)); setAvulsaEndGeo(cli.endereco||cli.endereco_padrao||""); }
    else { setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo(""); }
    setAvulsaStep(2);
  };
  const novoClienteAvulsa = () => { setAvulsaCliente(null); setAvulsaForm({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"" }); resetLocalizacao(); setAvulsaStep(2); };

  const parsearWhatsApp = async () => {
    if (!wpTexto.trim()) return; setWpParsing(true);
    // 1. Detectar link Google Maps no texto
    const coords = extrairCoordsDeTexto(wpTexto);
    if (coords) { setAvulsaLat(coords.lat); setAvulsaLng(coords.lng); notify("Localização detectada do Maps!"); }
    // 2. Chamar parser da API
    try {
      const r = await post("whatsapp_parser", { texto: wpTexto });
      if (r.sucesso && r.dados) {
        const d = r.dados;
        setAvulsaForm(f => ({ ...f, nome:d.nome||f.nome, telefone:d.telefone||f.telefone, endereco:d.endereco||f.endereco, complemento:d.complemento||f.complemento, bairro:d.bairro||f.bairro, cidade:d.cidade||f.cidade||"Ponta Porã", valor:d.valor||f.valor, obs:d.observacao||f.obs }));
        if (d.forma_pagamento) setAvulsaFormaPag(d.forma_pagamento);
        setAvulsaStep(2); notify(coords ? "Dados + localização extraídos!" : "Dados extraídos do WhatsApp!");
        // Se modo bairro, tentar auto-match com lista de bairros
        if (d.bairro && lojaModo?.tipo === 'bairro' && lojaModo.bairros?.length > 0) {
          const match = lojaModo.bairros.find(b => b.bairro_nome && d.bairro.toLowerCase().includes(b.bairro_nome.toLowerCase()));
          if (match) { setAvulsaTaxa(parseFloat(match.valor)); setAvulsaTaxaInfo("Bairro: " + match.bairro_nome); setAvulsaForm(f => ({...f, bairro: match.bairro_nome})); }
        } else if (d.bairro) {
          calcularTaxaAvulsa(d.bairro, d.valor);
        }
        // Se não achou coords no texto mas tem endereço, tentar geocoding automático
        if (!coords && (d.endereco || d.bairro)) {
          try {
            const geoR = await fetch(`https://mixupbr.com.br/loc/?acao=geocode&endereco=${encodeURIComponent((d.endereco||"")+" "+(d.bairro||""))}`);
            const geoD = await geoR.json();
            if (geoD.ok) { setAvulsaLat(geoD.latitude); setAvulsaLng(geoD.longitude); setAvulsaEndGeo(geoD.endereco_formatado||""); }
          } catch {}
        }
      } else notify("Não foi possível extrair dados", false);
    } catch { notify("Erro ao processar", false); } setWpParsing(false);
  };
  const colarDoClipboard = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) {
        setWpTexto(t); notify("Texto colado!");
        // Auto-detectar link Maps
        const coords = extrairCoordsDeTexto(t);
        if (coords) { setAvulsaLat(coords.lat); setAvulsaLng(coords.lng); notify(`📍 Localização detectada: ${coords.lat.toFixed(7)}, ${coords.lng.toFixed(7)}`); }
      }
    } catch { notify("Permita acesso à área de transferência", false); }
  };

  // ── Buscar modo de cobrança da loja ──
  const fetchLojaModo = async () => {
    setLojaModoLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/api/delivery/?modulo=loja_modo&loja_id=${lojaId||1}`);
      const d = await r.json();
      setLojaModo(d);
      // Se modo fixo, já setar a taxa direto
      if (d.tipo === 'fixo') { setAvulsaTaxa(d.valor_fixo); setAvulsaTaxaInfo("Taxa fixa: " + d.pacote_nome); }
    } catch { setLojaModo({ tipo:'km', bairros:[] }); }
    setLojaModoLoading(false);
  };

  const calcularTaxaAvulsa = async (bairroOverride, valorOverride) => {
    setAvulsaCalcTaxa(true);
    try {
      const lojaLat = parseFloat(loja?.latitude || loja?.lat || 0);
      const lojaLng = parseFloat(loja?.longitude || loja?.lng || 0);
      const cliLat = avulsaLat || 0;
      const cliLng = avulsaLng || 0;
      const bairro = bairroOverride || avulsaForm.bairro || "";
      const valorPedido = parseFloat(valorOverride || avulsaForm.valor || 0);

      // Se tem coordenadas da loja E do cliente → calcular_completo (km/bairro/fixo automático)
      if (lojaLat && lojaLng && cliLat && cliLng) {
        const r = await fetch("https://mixupbr.com.br/api/delivery/?modulo=calcular_completo", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lat_origem:lojaLat, lng_origem:lojaLng, lat_destino:cliLat, lng_destino:cliLng, loja_id:lojaId||1, valor_pedido:valorPedido, bairro })
        });
        const d = await r.json();
        if (d.taxa !== undefined) {
          setAvulsaTaxa(d.taxa);
          setAvulsaTaxaInfo(`${d.motivo || d.modo || ""} · ${d.distancia_km || 0}km · ~${d.tempo_estimado_min || 0}min`);
        } else {
          setAvulsaTaxa(null);
          setAvulsaTaxaInfo(d.error || "Erro no cálculo");
        }
      }
      // Se não tem coords da loja mas tem bairro → tenta bairro
      else if (bairro) {
        const r = await fetch("https://mixupbr.com.br/api/delivery/?modulo=calcular_completo", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lat_origem:-22.5348, lng_origem:-55.7249, lat_destino:cliLat||-22.5348, lng_destino:cliLng||-55.7249, loja_id:lojaId||1, valor_pedido:valorPedido, bairro })
        });
        const d = await r.json();
        if (d.taxa !== undefined) { setAvulsaTaxa(d.taxa); setAvulsaTaxaInfo(d.motivo || d.modo || ""); }
        else { setAvulsaTaxa(null); setAvulsaTaxaInfo("Configure coordenadas da loja"); }
      } else {
        setAvulsaTaxa(null); setAvulsaTaxaInfo("Preencha endereço ou obtenha localização");
      }
    } catch { setAvulsaTaxa(null); setAvulsaTaxaInfo("Erro ao calcular"); }
    setAvulsaCalcTaxa(false);
  };

  // Auto-calcular taxa quando localização do cliente muda (só modo KM)
  useEffect(() => {
    if (avulsaLat && avulsaLng && showAvulsa && lojaModo?.tipo !== 'bairro' && lojaModo?.tipo !== 'fixo') {
      calcularTaxaAvulsa();
    }
  }, [avulsaLat, avulsaLng]);

  const confirmarEntregaAvulsa = async () => {
    if (!avulsaForm.nome || !avulsaForm.telefone) { notify("Nome e telefone obrigatórios", false); return; }
    // Modo bairro: bairro selecionado basta. Modo km/fixo: endereço ou GPS
    const modoBairro = lojaModo?.tipo === 'bairro';
    if (!modoBairro && !avulsaForm.endereco && !avulsaLat) { notify("Endereço ou localização obrigatórios", false); return; }
    if (modoBairro && !avulsaForm.bairro) { notify("Selecione o bairro", false); return; }
    if (!avulsaForm.valor && !avulsaTaxa && avulsaTaxa !== 0) { notify("Informe o valor ou obtenha a taxa primeiro", false); return; }
    setAvulsaLoading(true);
    try {
      const codigo = "AVL" + String(Math.floor(Math.random() * 9000) + 1000);
      const end = [avulsaForm.endereco, avulsaForm.complemento, avulsaForm.bairro, avulsaForm.cidade].filter(Boolean).join(", ");
      const valorNum = parseFloat(avulsaForm.valor) || 0;
      const taxaNum = avulsaTaxa || 0;
      const res = await post("pedidos", { codigo, cliente_id:avulsaCliente?.id||1, loja_id:lojaId||1, status:"novo",
        subtotal:valorNum.toFixed(2), taxa_entrega:taxaNum.toFixed(2), desconto:"0", total:(valorNum+taxaNum).toFixed(2),
        forma_pagamento:avulsaFormaPag||"dinheiro", endereco_entrega:end, telefone_entrega:avulsaForm.telefone,
        cliente_nome:avulsaForm.nome||"Avulso",
        observacao:`[ENTREGA AVULSA]${avulsaJaPagou?" [PAGO ONLINE]":""} Cliente: ${avulsaForm.nome} • Tel: ${avulsaForm.telefone}${avulsaJaPagou?" • PAGO ("+avulsaFormaPag.toUpperCase()+")":""}${!avulsaJaPagou&&avulsaFormaPag==="dinheiro"&&avulsaTroco?" • TROCO p/ R$"+avulsaTroco:""}${avulsaForm.obs ? " • "+avulsaForm.obs : ""}`,
        troco_para: (!avulsaJaPagou && avulsaFormaPag==="dinheiro" && avulsaTroco) ? avulsaTroco : null,
        itens:[{ produto_id:1, nome:`Entrega avulsa - ${avulsaForm.nome}`, quantidade:1, preco_unitario:valorNum.toFixed(2), preco_total:valorNum.toFixed(2) }],
        cliente_nome_avulso:avulsaForm.nome, cliente_tel_avulso:avulsaForm.telefone,
        latitude: avulsaLat || null, longitude: avulsaLng || null });
      if (res.id || res.success) {
        notify(`Entrega avulsa #${codigo} criada!`);
        if (!avulsaCliente && avulsaForm.nome) post("loja_config", { acao:"salvar_cliente_avulso", loja_id:lojaId||1, nome:avulsaForm.nome, telefone:avulsaForm.telefone, endereco:avulsaForm.endereco, bairro:avulsaForm.bairro, cidade:avulsaForm.cidade, latitude:avulsaLat, longitude:avulsaLng }).catch(()=>{});
        if (avulsaCliente?.id && avulsaLat) post("loja_config", { acao:"atualizar_loc_cliente", cliente_id:avulsaCliente.id, latitude:avulsaLat, longitude:avulsaLng, endereco:avulsaForm.endereco }).catch(()=>{});
        onRefresh(); setShowAvulsa(false); setAvulsaStep(1); setAvulsaBusca(""); setAvulsaResultados([]);
        setWpTexto(""); setAvulsaTaxa(null); setAvulsaTaxaInfo(""); setAvulsaFormaPag("dinheiro"); setAvulsaJaPagou(false); setAvulsaTroco(""); resetLocalizacao();
      } else notify(res.error || "Erro ao criar", false);
    } catch { notify("Erro de conexão", false); } setAvulsaLoading(false);
  };

  // ── Cálculos vendas ──
  const vendasHoje = orders.filter(o => o.criado_em?.startsWith(new Date().toISOString().slice(0,10)));
  const vendasDone = vendasHoje.filter(o => DONE_STATUS.includes(o.status));
  const totalVendas = vendasDone.reduce((s, o) => s + Number(o.total||0), 0);
  const vendaPix = vendasDone.filter(o => o.forma_pagamento==="pix").reduce((s,o) => s+Number(o.total||0), 0);
  const vendaCartao = vendasDone.filter(o => ["credito","debito"].includes(o.forma_pagamento)).reduce((s,o) => s+Number(o.total||0), 0);
  const vendaDinheiro = vendasDone.filter(o => o.forma_pagamento==="dinheiro").reduce((s,o) => s+Number(o.total||0), 0);
  const vendaCash = vendasDone.filter(o => o.forma_pagamento==="cashback").reduce((s,o) => s+Number(o.total||0), 0);

  const sel = selected ? orders.find(o => o.id === selected.id) : null;
  const stLabel = (st) => ({ novo:_("stNovo"), aceito:_("stAceito"), preparando:_("stPreparando"), pronto:_("stPronto"), buscando_entregador:_("stBuscando"), em_entrega:_("stEmEntrega"), entregue:_("stEntregue"), cancelado:_("stCancelado") }[st] || st);

  const iS = { width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" };

  // ── Seções da lista ──
  const SECTIONS = [
    { key:"pendente", label:"Pendente", color:C.red, items: orders.filter(o => o.status === "novo") },
    { key:"aceito",   label:"Aceito",  color:C.orange, items: orders.filter(o => ["aceito","preparando"].includes(o.status)) },
    { key:"entregador",label:"Aguardando entregador", color:"#3B82F6", items: orders.filter(o => ["pronto","buscando_entregador"].includes(o.status)) },
    { key:"rota",     label:"Em entrega", color:C.greenLight, items: orders.filter(o => o.status === "em_entrega") },
    { key:"concluido",label:"Concluído", color:"#6B7280", items: orders.filter(o => ["entregue","cancelado"].includes(o.status)).slice(0,10) },
  ];

  // Busca
  const buscaLower = busca.toLowerCase().trim();
  const filtrarBusca = (arr) => buscaLower ? arr.filter(o =>
    (o.codigo||"").toLowerCase().includes(buscaLower) ||
    (o.cliente_nome||"").toLowerCase().includes(buscaLower) ||
    (o.observacao||"").toLowerCase().includes(buscaLower)
  ) : arr;

  // ──────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────
  return (
    <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 32px)", animation:"fadeUp .3s ease-out", overflow:"hidden" }}
      onMouseMove={markInteracting} onKeyDown={markInteracting}>

      <audio ref={somRef} src="/mixup-lojista/notification.mp3" preload="auto" />

      {toast && (
        <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", zIndex:999,
          background: toast.ok ? C.green : C.red, color:"#fff", padding:"12px 28px",
          borderRadius:12, fontWeight:700, fontSize:14, animation:"slideDown .3s ease-out", whiteSpace:"nowrap" }}>
          {toast.msg}
        </div>
      )}

      {/* ══ MODALS ══ */}
      {showVendas && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setShowVendas(false)}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:420, border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out" }} onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:800, fontSize:22, marginBottom:20 }}>Vendas de Hoje</h3>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
              {[{label:"PIX",value:fmt(vendaPix),color:C.green},{label:"Cartão",value:fmt(vendaCartao),color:C.red},{label:"Dinheiro",value:fmt(vendaDinheiro),color:C.yellow},{label:"Cashback",value:fmt(vendaCash),color:"#8B5CF6"}].map((v,i) => (
                <div key={i} style={{ padding:16, background:C.bg, borderRadius:14, border:`1px solid ${C.border}` }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:v.color, marginBottom:10 }} />
                  <div style={{ fontSize:20, fontWeight:800 }}>{v.value}</div>
                  <div style={{ fontSize:12, color:C.textMuted, marginTop:4 }}>{v.label}</div>
                </div>
              ))}
            </div>
            <div style={{ padding:18, background:`${C.green}10`, borderRadius:14, border:`1px solid ${C.green}30`, textAlign:"center" }}>
              <div style={{ fontSize:13, color:C.textMuted, marginBottom:4 }}>Total do Dia</div>
              <div style={{ fontSize:28, fontWeight:800, color:C.green }}>{fmt(totalVendas)}</div>
              <div style={{ fontSize:12, color:C.textMuted, marginTop:4 }}>{vendasDone.length} pedidos</div>
            </div>
            <button onClick={() => setShowVendas(false)} style={{ width:"100%", padding:14, borderRadius:12, border:"none", background:C.border, color:C.textSoft, fontWeight:700, fontSize:14, marginTop:16, cursor:"pointer" }}>Fechar</button>
          </div>
        </div>
      )}

      {showSaldo && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setShowSaldo(false)}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:360, border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out", textAlign:"center" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:40, marginBottom:8 }}>💳</div>
            <h3 style={{ fontWeight:800, fontSize:20, marginBottom:6 }}>Saldo de Recarga</h3>
            <p style={{ fontSize:13, color:C.textMuted, marginBottom:20 }}>Crédito adicionado pelo administrador.<br/>Debitado a cada entrega.</p>
            <div style={{ padding:24, background:`${C.blue}10`, borderRadius:16, border:`1px solid ${C.blue}30`, marginBottom:20 }}>
              <div style={{ fontSize:36, fontWeight:900, color:C.blue }}>{saldo !== null ? fmt(saldo) : "—"}</div>
            </div>
            <button onClick={() => setShowSaldo(false)} style={{ width:"100%", padding:13, borderRadius:12, border:"none", background:C.border, color:C.textSoft, fontWeight:700, fontSize:14, cursor:"pointer" }}>Fechar</button>
          </div>
        </div>
      )}

      {/* MODAL ENTREGA AVULSA */}
      {showAvulsa && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => { setShowAvulsa(false); setAvulsaStep(1); setAvulsaBusca(""); setAvulsaResultados([]); setWpTexto(""); setAvulsaTaxa(null); setAvulsaTaxaInfo(""); setAvulsaFormaPag("dinheiro"); resetLocalizacao(); }}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:480, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out" }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <div>
                <h3 style={{ fontWeight:800, fontSize:20, marginBottom:2 }}>Entrega Avulsa</h3>
                <p style={{ fontSize:12, color:C.textMuted }}>{avulsaStep===1?"Busque um cliente ou crie novo":avulsaStep===3?"Cadastrar novo cliente":"Dados da entrega"}</p>
              </div>
              {avulsaStep > 1 && <button onClick={() => setAvulsaStep(avulsaStep-1)} style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.textSoft, fontSize:12, fontWeight:600, cursor:"pointer" }}>← Voltar</button>}
            </div>

            {avulsaStep === 1 && (<div>
              <div style={{ padding:14, borderRadius:12, background:"#075E5410", border:"1px solid #075E5430", marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:"#25D366" }}>Colar do WhatsApp</span>
                  <button onClick={colarDoClipboard} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #25D36640", background:"#25D36615", color:"#25D366", cursor:"pointer", fontWeight:700, fontSize:11 }}>Colar</button>
                </div>
                <textarea value={wpTexto} onChange={e => setWpTexto(e.target.value)}
                  onPaste={e => {
                    // onPaste pega texto COMPLETO de uma vez — mais preciso que onChange
                    setTimeout(() => {
                      const v = e.target.value;
                      if (v && v.length > 15) {
                        const coords = extrairCoordsDeTexto(v);
                        if (coords) { setAvulsaLat(coords.lat); setAvulsaLng(coords.lng); notify(`📍 Localização detectada: ${coords.lat.toFixed(7)}, ${coords.lng.toFixed(7)}`); }
                      }
                    }, 100);
                  }}
                  rows={3} placeholder={"Cole a mensagem do WhatsApp aqui...\nAceita: texto, link Google Maps, coordenadas"} style={{ ...iS, height:70, resize:"vertical", fontSize:12 }} />
                <button onClick={parsearWhatsApp} disabled={wpParsing||!wpTexto.trim()} style={{ marginTop:8, width:"100%", padding:10, borderRadius:8, border:"none", background:"#25D366", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", opacity:(wpParsing||!wpTexto.trim())?0.5:1 }}>{wpParsing?"Extraindo...":"Extrair Dados e Preencher"}</button>
              </div>
              <div style={{ textAlign:"center", fontSize:11, color:C.textMuted, marginBottom:12, letterSpacing:1 }}>— OU BUSQUE NO SISTEMA —</div>
              <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                <input placeholder="Nome ou telefone..." value={avulsaBusca} onChange={e=>setAvulsaBusca(e.target.value)} onKeyDown={e=>e.key==="Enter"&&buscarCliente()} style={{ ...iS, flex:1 }} />
                <button onClick={buscarCliente} disabled={avulsaLoading} style={{ padding:"10px 18px", borderRadius:8, border:"none", background:C.blue, color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>{avulsaLoading?"...":"Buscar"}</button>
              </div>
              {avulsaResultados.length > 0 && <div style={{ marginBottom:12 }}>{avulsaResultados.map(cli => (
                <div key={cli.id} onClick={() => selecionarClienteAvulsa(cli)} style={{ padding:"12px 14px", borderRadius:12, border:`1px solid ${C.border}`, background:C.bg, marginBottom:8, cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:C.card, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0, overflow:"hidden" }}>
                    {cli.foto_ref_url ? <img src={"https://mixupbr.com.br"+cli.foto_ref_url} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (cli.origem==="avulso"?"🏠":"👤")}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{cli.nome}</div>
                    <div style={{ fontSize:12, color:C.textMuted }}>{cli.telefone}</div>
                  </div>
                  <span style={{ fontSize:12, color:C.blue, fontWeight:700 }}>→</span>
                </div>
              ))}</div>}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={novoClienteAvulsa} style={{ flex:1, padding:13, borderRadius:12, border:`1.5px dashed ${C.border}`, background:"transparent", color:C.textSoft, fontWeight:700, fontSize:13, cursor:"pointer" }}>Sem cadastro</button>
                <button onClick={() => { setAvulsaCliente(null); setAvulsaForm({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"" }); setAvulsaStep(3); }} style={{ flex:1, padding:13, borderRadius:12, border:"none", background:`${C.blue}20`, color:C.blue, fontWeight:700, fontSize:13, cursor:"pointer" }}>+ Cadastrar</button>
              </div>

              {/* ═══ LOCALIZAÇÃO já no Step 1 ═══ */}
              <div style={{ padding:12, borderRadius:12, background:`#22C55E08`, border:`1px solid #22C55E25`, marginTop:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:C.greenLight, letterSpacing:"0.04em" }}>📍 LOCALIZAÇÃO DO CLIENTE</span>
                  {avulsaLat && avulsaLng && <span style={{ fontSize:10, fontWeight:700, color:C.greenLight, background:`${C.greenLight}18`, padding:"2px 8px", borderRadius:6 }}>✓ Obtida</span>}
                </div>
                {avulsaLat && avulsaLng ? (
                  <div>
                    <div style={{ borderRadius:10, overflow:"hidden", border:`1px solid ${C.border}`, height:100 }}>
                      <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${avulsaLat},${avulsaLng}&zoom=16&size=460x100&markers=color:red|${avulsaLat},${avulsaLng}&key=AIzaSyDKRGxT4nBsTYRkSJ_zrmMgb8cYI00WGK8`}
                        style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="Mapa" />
                    </div>
                    {avulsaEndGeo && <div style={{ fontSize:10, color:C.greenLight, marginTop:4, fontWeight:600 }}>📍 {avulsaEndGeo}</div>}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4 }}>
                      <span style={{ fontSize:9, color:C.textMuted }}>Lat: {avulsaLat.toFixed(5)} · Lng: {avulsaLng.toFixed(5)}</span>
                      <button onClick={() => { setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo(""); }} style={{ padding:"3px 8px", borderRadius:5, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:9, cursor:"pointer" }}>✕ Limpar</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {!locCode ? (
                      <button onClick={criarLinkLoc} style={{ width:"100%", padding:10, borderRadius:10, border:`1.5px dashed ${C.greenLight}50`, background:`${C.greenLight}08`, color:C.greenLight, fontWeight:700, fontSize:12, cursor:"pointer", marginBottom:6, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                        📲 Gerar link p/ cliente (GPS)
                      </button>
                    ) : (
                      <div style={{ padding:10, borderRadius:10, background:C.card, border:`1px solid ${C.border}`, marginBottom:6 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                          <span style={{ fontSize:11, fontWeight:700, color: locStatus==="recebido" ? C.greenLight : C.yellow }}>
                            {locStatus === "recebido" ? "✅ Localização recebida!" : "⏳ Aguardando cliente..."}
                          </span>
                          {locPolling && <div style={{ width:14, height:14, border:"2px solid "+C.border, borderTopColor:C.greenLight, borderRadius:"50%", animation:"spin .8s linear infinite" }} />}
                        </div>
                        <div style={{ fontSize:10, color:C.textMuted, wordBreak:"break-all", marginBottom:6 }}>{locLink}</div>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={async () => { try { await navigator.clipboard.writeText(locMsgWp); notify("Mensagem copiada!"); } catch {} }}
                            style={{ flex:1, padding:6, borderRadius:6, border:`1px solid #25D36640`, background:"#25D36612", color:"#25D366", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                            Copiar p/ WhatsApp
                          </button>
                          <button onClick={resetLocalizacao} style={{ padding:6, borderRadius:6, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:10, cursor:"pointer" }}>✕</button>
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize:9, color:C.textMuted, textAlign:"center" }}>Link Maps no WhatsApp? Cole no campo acima — detectamos automaticamente</div>
                  </div>
                )}
              </div>
            </div>)}

            {avulsaStep === 3 && (<div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Nome *</label><input value={avulsaForm.nome} onChange={e=>setAvulsaForm({...avulsaForm, nome:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Telefone</label><input value={avulsaForm.telefone} onChange={e=>setAvulsaForm({...avulsaForm, telefone:e.target.value})} style={iS} /></div>
              </div>
              <div style={{ marginBottom:10 }}><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Endereço</label><input value={avulsaForm.endereco} onChange={e=>setAvulsaForm({...avulsaForm, endereco:e.target.value})} style={iS} /></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Bairro</label><input value={avulsaForm.bairro} onChange={e=>setAvulsaForm({...avulsaForm, bairro:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Cidade</label><input value={avulsaForm.cidade} onChange={e=>setAvulsaForm({...avulsaForm, cidade:e.target.value})} style={iS} /></div>
              </div>

              {/* Localização — campos editáveis + geocoding */}
              <div style={{ padding:12, borderRadius:10, background:`#22C55E08`, border:`1px solid #22C55E25`, marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:C.greenLight, letterSpacing:"0.04em" }}>📍 LOCALIZAÇÃO</span>
                  {avulsaLat && avulsaLng && <span style={{ fontSize:10, fontWeight:700, color:C.greenLight, background:`${C.greenLight}18`, padding:"2px 8px", borderRadius:6 }}>✓ Obtida</span>}
                </div>
                {avulsaLat && avulsaLng && (
                  <div style={{ borderRadius:8, overflow:"hidden", border:`1px solid ${C.border}`, height:80, marginBottom:8 }}>
                    <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${avulsaLat},${avulsaLng}&zoom=16&size=460x80&markers=color:red|${avulsaLat},${avulsaLng}&key=AIzaSyDKRGxT4nBsTYRkSJ_zrmMgb8cYI00WGK8`}
                      style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="Mapa" />
                  </div>
                )}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                  <div>
                    <label style={{ fontSize:10, fontWeight:700, color:C.greenLight, display:"block", marginBottom:4 }}>Latitude</label>
                    <input type="number" step="any" value={avulsaLat||""} onChange={e => setAvulsaLat(e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="-22.53000" style={{ ...iS, fontSize:11, padding:"7px 10px", borderColor:`#22C55E40` }} />
                  </div>
                  <div>
                    <label style={{ fontSize:10, fontWeight:700, color:C.greenLight, display:"block", marginBottom:4 }}>Longitude</label>
                    <input type="number" step="any" value={avulsaLng||""} onChange={e => setAvulsaLng(e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="-55.72000" style={{ ...iS, fontSize:11, padding:"7px 10px", borderColor:`#22C55E40` }} />
                  </div>
                </div>
                <button onClick={geocodarEndereco} disabled={geoLoading || !avulsaForm.endereco}
                  style={{ width:"100%", padding:7, borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}08`, color:C.blue, fontWeight:700, fontSize:11, cursor:"pointer", opacity:(geoLoading||!avulsaForm.endereco)?0.5:1 }}>
                  {geoLoading ? "Buscando..." : "🔍 Buscar pelo endereço acima"}
                </button>
              </div>

              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setAvulsaStep(1)} style={{ flex:1, padding:13, borderRadius:12, border:`1px solid ${C.border}`, background:"transparent", color:C.textSoft, fontWeight:700, cursor:"pointer" }}>Cancelar</button>
                <button onClick={async () => {
                  if (!avulsaForm.nome) { notify("Nome obrigatório", false); return; }
                  try {
                    const resp = await fetch("https://mixupbr.com.br/api/loja_config/index.php", { method:"POST", headers:{"Content-Type":"application/json"},
                      body:JSON.stringify({ acao:"salvar_cliente_avulso", loja_id:lojaId||1, nome:avulsaForm.nome, telefone:avulsaForm.telefone, endereco:avulsaForm.endereco, bairro:avulsaForm.bairro, cidade:avulsaForm.cidade, obs_entrega:avulsaForm.obs||"", latitude:avulsaLat, longitude:avulsaLng }) });
                    const txt = await resp.text();
                    if (!txt) { notify("API vazia", false); return; }
                    let salvRes; try { salvRes = JSON.parse(txt); } catch { notify("API: "+txt.slice(0,120), false); return; }
                    if (salvRes.error) { notify("Erro: "+salvRes.error, false); return; }
                    notify("Cliente cadastrado!"); setAvulsaCliente({ ...avulsaForm, id:salvRes.id, origem:"avulso", latitude:avulsaLat, longitude:avulsaLng }); setAvulsaStep(2);
                  } catch(err) { notify("Erro: "+(err?.message||""), false); }
                }} style={{ flex:2, padding:13, borderRadius:12, border:"none", background:C.blue, color:"#fff", fontWeight:700, cursor:"pointer" }}>Salvar e continuar</button>
              </div>
            </div>)}

            {avulsaStep === 2 && (<div>
              {avulsaCliente && <div style={{ padding:"10px 14px", borderRadius:10, background:`${C.blue}10`, border:`1px solid ${C.blue}30`, marginBottom:16, fontSize:13 }}><strong>{avulsaCliente.nome}</strong> · {avulsaCliente.telefone}</div>}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Nome *</label><input value={avulsaForm.nome} onChange={e=>setAvulsaForm({...avulsaForm, nome:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Telefone</label><input value={avulsaForm.telefone} onChange={e=>setAvulsaForm({...avulsaForm, telefone:e.target.value})} style={iS} /></div>
              </div>
              <div style={{ marginBottom:10 }}><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Endereço *</label><input value={avulsaForm.endereco} onChange={e=>setAvulsaForm({...avulsaForm, endereco:e.target.value})} style={iS} /></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Bairro</label><input value={avulsaForm.bairro} onChange={e=>setAvulsaForm({...avulsaForm, bairro:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Valor (R$)</label><input type="number" value={avulsaForm.valor} onChange={e=>setAvulsaForm({...avulsaForm, valor:e.target.value})} placeholder="Valor do pedido (opcional)" style={iS} /></div>
              </div>

              {/* ═══ LOCALIZAÇÃO — 3 métodos ═══ */}
              <div style={{ padding:12, borderRadius:12, background:`#22C55E08`, border:`1px solid #22C55E25`, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:C.greenLight, letterSpacing:"0.04em" }}>📍 LOCALIZAÇÃO</span>
                  {avulsaLat && avulsaLng && <span style={{ fontSize:10, fontWeight:700, color:C.greenLight, background:`${C.greenLight}18`, padding:"2px 8px", borderRadius:6 }}>✓ Obtida</span>}
                </div>

                {/* Mini mapa preview quando tem lat/lng */}
                {avulsaLat && avulsaLng ? (
                  <div style={{ marginBottom:8 }}>
                    <div style={{ borderRadius:10, overflow:"hidden", border:`1px solid ${C.border}`, height:120, position:"relative" }}>
                      <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${avulsaLat},${avulsaLng}&zoom=16&size=460x120&markers=color:red|${avulsaLat},${avulsaLng}&key=AIzaSyDKRGxT4nBsTYRkSJ_zrmMgb8cYI00WGK8`}
                        style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="Mapa" />
                    </div>
                    {avulsaEndGeo && <div style={{ fontSize:10, color:C.greenLight, marginTop:4, fontWeight:600 }}>📍 {avulsaEndGeo}</div>}
                    <div style={{ fontSize:9, color:C.textMuted, marginTop:2 }}>Lat: {avulsaLat.toFixed(5)} · Lng: {avulsaLng.toFixed(5)}</div>
                    <button onClick={() => { setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo(""); }} style={{ marginTop:6, padding:"4px 10px", borderRadius:6, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:10, fontWeight:600, cursor:"pointer" }}>✕ Limpar localização</button>
                  </div>
                ) : (
                  <div>
                    {/* Método 1: Pedir localização via link */}
                    {!locCode ? (
                      <button onClick={criarLinkLoc} style={{ width:"100%", padding:10, borderRadius:10, border:`1.5px dashed ${C.greenLight}50`, background:`${C.greenLight}08`, color:C.greenLight, fontWeight:700, fontSize:12, cursor:"pointer", marginBottom:8, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                        📲 Gerar link p/ cliente (GPS)
                      </button>
                    ) : (
                      <div style={{ padding:10, borderRadius:10, background:C.card, border:`1px solid ${C.border}`, marginBottom:8 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                          <span style={{ fontSize:11, fontWeight:700, color: locStatus==="recebido" ? C.greenLight : C.yellow }}>
                            {locStatus === "recebido" ? "✅ Localização recebida!" : "⏳ Aguardando cliente..."}
                          </span>
                          {locPolling && <div style={{ width:14, height:14, border:"2px solid "+C.border, borderTopColor:C.greenLight, borderRadius:"50%", animation:"spin .8s linear infinite" }} />}
                        </div>
                        <div style={{ fontSize:10, color:C.textMuted, wordBreak:"break-all", marginBottom:6 }}>{locLink}</div>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={async () => { try { await navigator.clipboard.writeText(locMsgWp); notify("Mensagem copiada!"); } catch {} }}
                            style={{ flex:1, padding:6, borderRadius:6, border:`1px solid #25D36640`, background:"#25D36612", color:"#25D366", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                            Copiar p/ WhatsApp
                          </button>
                          <button onClick={() => { resetLocalizacao(); }} style={{ padding:6, borderRadius:6, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:10, cursor:"pointer" }}>✕</button>
                        </div>
                      </div>
                    )}

                    {/* Método 2: Geocoding automático */}
                    <button onClick={geocodarEndereco} disabled={geoLoading || !avulsaForm.endereco}
                      style={{ width:"100%", padding:8, borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}08`, color:C.blue, fontWeight:700, fontSize:11, cursor:"pointer", marginBottom:4, opacity:(geoLoading||!avulsaForm.endereco)?0.5:1 }}>
                      {geoLoading ? "Buscando..." : "🔍 Buscar no mapa pelo endereço"}
                    </button>

                    <div style={{ fontSize:9, color:C.textMuted, textAlign:"center", marginTop:4 }}>
                      Link Maps no WhatsApp? Cole o texto acima que detectamos automaticamente
                    </div>
                  </div>
                )}
              </div>

              <div style={{ padding:12, borderRadius:10, background:`${C.blue}08`, border:`1px solid ${C.blue}25`, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:C.blue }}>TAXA DE ENTREGA {lojaModo?.tipo === 'bairro' ? "(por bairro)" : lojaModo?.tipo === 'fixo' ? "(fixa)" : avulsaLat && avulsaLng ? "(auto)" : ""}</span>
                  {lojaModo?.tipo === 'km' && <button onClick={() => calcularTaxaAvulsa()} disabled={avulsaCalcTaxa} style={{ padding:"4px 12px", borderRadius:6, border:`1px solid ${C.blue}40`, background:`${C.blue}15`, color:C.blue, cursor:"pointer", fontWeight:700, fontSize:11 }}>{avulsaCalcTaxa?"Calculando...":"Recalcular"}</button>}
                </div>

                {/* Modo BAIRRO: dropdown de bairros */}
                {lojaModo?.tipo === 'bairro' && lojaModo.bairros?.length > 0 && (
                  <div style={{ marginBottom:10 }}>
                    <label style={{ fontSize:10, fontWeight:700, color:C.textMuted, display:"block", marginBottom:4 }}>Selecione o bairro:</label>
                    <select value={avulsaForm.bairro} onChange={e => {
                      const sel = e.target.value;
                      const b = lojaModo.bairros.find(x => x.bairro_nome === sel);
                      setAvulsaForm({...avulsaForm, bairro: sel});
                      if (b) { setAvulsaTaxa(parseFloat(b.valor)); setAvulsaTaxaInfo("Bairro: " + b.bairro_nome); }
                      else { setAvulsaTaxa(null); setAvulsaTaxaInfo(""); }
                    }} style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${C.blue}40`, background:C.card, color:C.text, fontSize:13, fontWeight:600, cursor:"pointer", outline:"none" }}>
                      <option value="">-- Selecione o bairro --</option>
                      {lojaModo.bairros.map(b => (
                        <option key={b.id} value={b.bairro_nome}>{b.bairro_nome} — R$ {parseFloat(b.valor).toFixed(2)}</option>
                      ))}
                    </select>
                  </div>
                )}

                {avulsaTaxa !== null ? (
                  <div>
                    <span style={{ fontSize:22, fontWeight:800, color:avulsaTaxa===0?C.greenLight:C.text }}>{avulsaTaxa===0?"GRÁTIS":fmt(avulsaTaxa)}</span>
                    {avulsaTaxaInfo && <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>{avulsaTaxaInfo}</div>}
                  </div>
                ) : (
                  <span style={{ fontSize:11, color:C.textMuted }}>
                    {lojaModo?.tipo === 'bairro' ? "Selecione o bairro acima" : avulsaLat ? "Clique Recalcular" : "Obtenha localização ou preencha bairro"}
                  </span>
                )}
              </div>
              {/* ── Já pagou? ── */}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:6 }}>Já pagou?</label>
                <div style={{ display:"flex", gap:6 }}>
                  {[{k:false,l:"Não — cobrar na entrega",c:C.yellow},{k:true,l:"Sim — já pago",c:C.greenLight}].map(op => (
                    <button key={String(op.k)} onClick={() => setAvulsaJaPagou(op.k)}
                      style={{ flex:1, padding:"9px 6px", borderRadius:8, border:`1px solid ${avulsaJaPagou===op.k?op.c:C.border}`,
                        background:avulsaJaPagou===op.k?op.c+"20":"transparent", color:avulsaJaPagou===op.k?op.c:C.textMuted,
                        cursor:"pointer", fontSize:11, fontWeight:700 }}>
                      {op.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Forma de pagamento ── */}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:6 }}>
                  {avulsaJaPagou ? "Pago via" : "Forma de pagamento"}
                </label>
                <div style={{ display:"flex", gap:6 }}>
                  {[{k:"dinheiro",l:"Dinheiro",c:C.yellow},{k:"pix",l:"PIX",c:C.green},{k:"credito",l:"Crédito",c:C.red},{k:"debito",l:"Débito",c:C.blue}].map(fp => (
                    <button key={fp.k} onClick={() => setAvulsaFormaPag(fp.k)} style={{ flex:1, padding:"8px 4px", borderRadius:8, border:`1px solid ${avulsaFormaPag===fp.k?fp.c:C.border}`, background:avulsaFormaPag===fp.k?fp.c+"20":"transparent", color:avulsaFormaPag===fp.k?fp.c:C.textMuted, cursor:"pointer", fontSize:11, fontWeight:700 }}>{fp.l}</button>
                  ))}
                </div>
              </div>

              {/* ── Troco (só se não pagou e forma = dinheiro) ── */}
              {!avulsaJaPagou && avulsaFormaPag === "dinheiro" && (
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.yellow, display:"block", marginBottom:5 }}>Troco para quanto? (R$)</label>
                  <input type="number" value={avulsaTroco} onChange={e => setAvulsaTroco(e.target.value)}
                    placeholder="Ex: 100 (deixe vazio se não precisa)" style={{ ...iS, borderColor:`${C.yellow}40` }} />
                </div>
              )}

              {/* ── Observação / Referência ── */}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Observação / Referência do local</label>
                <textarea value={avulsaForm.obs||""} onChange={e => setAvulsaForm({...avulsaForm, obs:e.target.value})}
                  placeholder="Ex: portão azul, ligar antes, entrar pela lateral..."
                  style={{ ...iS, height:56, resize:"vertical" }} />
              </div>

              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => { setShowAvulsa(false); setAvulsaStep(1); }} style={{ flex:1, padding:13, borderRadius:12, border:`1px solid ${C.border}`, background:"transparent", color:C.textSoft, fontWeight:700, cursor:"pointer" }}>Cancelar</button>
                <button onClick={confirmarEntregaAvulsa} disabled={avulsaLoading} style={{ flex:2, padding:13, borderRadius:12, border:"none", background:`linear-gradient(135deg,${C.green},${C.greenLight})`, color:"#fff", fontWeight:700, cursor:"pointer" }}>{avulsaLoading?"Criando...":"Criar Entrega"}</button>
              </div>
            </div>)}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════
          LAYOUT PRINCIPAL — iFood clássico
      ═══════════════════════════════════════ */}
      <div style={{ display:"flex", flex:1, minHeight:0, overflow:"hidden" }}>

        {/* ═══ PAINEL ESQUERDO — Lista de pedidos ═══ */}
        <div style={{ width:300, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", flexShrink:0 }}>

          {/* Header com tabs */}
          <div style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ padding:"4px 12px", background:C.red, color:"#fff", fontSize:11, fontWeight:700, borderRadius:14, display:"flex", alignItems:"center", gap:4 }}>
              Agora
              {orders.filter(o=>!["entregue","cancelado"].includes(o.status)).length > 0 && (
                <span style={{ background:"#fff", color:C.red, fontSize:9, fontWeight:800, padding:"0 5px", borderRadius:8 }}>
                  {orders.filter(o=>!["entregue","cancelado"].includes(o.status)).length}
                </span>
              )}
            </div>
          </div>

          {/* Busca + Filtros */}
          <div style={{ padding:"8px 10px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:6 }}>
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar pedido..."
              style={{ flex:1, padding:"7px 10px", background:C.card, borderRadius:8, border:`1px solid ${C.border}`, color:C.text, fontSize:11, outline:"none", fontFamily:"inherit" }} />
          </div>

          {/* Lista com seções */}
          <div style={{ flex:1, overflowY:"auto", padding:"6px 8px" }} onScroll={markInteracting}>
            {SECTIONS.map(sec => {
              const items = filtrarBusca(sec.items);
              if (items.length === 0 && sec.key !== "pendente") return null;
              return (
                <div key={sec.key} style={{ marginBottom:4 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 4px 4px" }}>
                    <span style={{ fontSize:10, fontWeight:700, color:sec.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>{sec.label}</span>
                    <span style={{ fontSize:10, color:C.textMuted }}>{items.length} pedido{items.length !== 1 ? "s" : ""}</span>
                  </div>
                  {items.length === 0 && sec.key === "pendente" && (
                    <div style={{ textAlign:"center", padding:"20px 10px", color:C.textMuted, fontSize:11 }}>Nenhum pedido novo</div>
                  )}
                  {items.map(o => {
                    const isNovo = o.status === "novo";
                    const min = minutosDesde(o.criado_em);
                    const cor = isNovo ? timerCor(min) : sec.color;
                    const isSel = sel?.id === o.id;
                    const isAvulsa = o.observacao?.includes("[ENTREGA AVULSA]");
                    const naoLidos = (chatNaoLidos[o.id]||0) + (chatNaoLidosEnt[o.id]||0);
                    const stBadge = { aceito:{c:C.blue,l:"ACEITO"}, preparando:{c:C.orange,l:"PREPARO"}, pronto:{c:"#3B82F6",l:"PRONTO"}, buscando_entregador:{c:"#3B82F6",l:"AGUARD"}, em_entrega:{c:C.greenLight,l:"ROTA"}, entregue:{c:"#6B7280",l:"ENTREGUE"}, cancelado:{c:C.red,l:"CANCELADO"} };
                    const badge = stBadge[o.status];

                    return (
                      <div key={o.id} onClick={() => { markInteracting(); setSelected(o); }}
                        style={{
                          background: isSel ? `${C.red}12` : C.card,
                          borderRadius: 10, padding: "10px 10px", marginBottom: 4,
                          borderLeft: `4px solid ${cor}`,
                          border: isSel ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                          cursor: "pointer", transition: "all .15s", position: "relative",
                        }}>
                        {/* Timer bar */}
                        {isNovo && (
                          <div style={{ position:"absolute", top:0, left:0, right:0, height:3, borderRadius:"10px 10px 0 0", overflow:"hidden", background:`${C.border}40` }}>
                            <div style={{ height:"100%", width:`${timerPct(min)}%`, background:cor, transition:"width 1s", borderRadius:3 }} />
                          </div>
                        )}
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <span style={{ fontWeight:800, fontSize:12, color:C.text }}>#{o.codigo}</span>
                            {naoLidos > 0 && <span style={{ fontSize:8, fontWeight:700, color:"#fff", background:C.red, padding:"0 4px", borderRadius:4 }}>💬{naoLidos}</span>}
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            {isAvulsa && <span style={{ fontSize:8, fontWeight:700, color:"#8B5CF6", background:"#8B5CF615", padding:"1px 4px", borderRadius:3 }}>AVL</span>}
                            {isNovo ? (
                              <span style={{ fontSize:10, fontWeight:800, color:cor }}>{min < 1 ? "agora" : `${min} min`}</span>
                            ) : badge && (
                              <span style={{ fontSize:8, fontWeight:700, color:badge.c, background:badge.c+"18", padding:"1px 5px", borderRadius:3 }}>{badge.l}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize:11, color:C.textSoft, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                          {o.cliente_nome || (isAvulsa ? o.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim() : `#${o.cliente_id}`)}
                          {" · "}{(o.itens||[]).map(it => `${it.quantidade}x ${it.nome}`).join(", ").slice(0,40) || "..."}
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <span style={{ fontSize:9, fontWeight:700, color:C.textMuted, background:`${C.textMuted}12`, padding:"1px 5px", borderRadius:3 }}>{(o.forma_pagamento||"").toUpperCase()||"—"}</span>
                            <span style={{ fontWeight:800, fontSize:13, color:C.green }}>{fmt(o.total)}</span>
                          </div>
                          {isNovo && (
                            <button onClick={e => { e.stopPropagation(); pararSom(); onAdvance(o.id); setTimeout(()=>onAdvance(o.id),600); }}
                              style={{ padding:"4px 12px", borderRadius:7, border:"none", color:"#fff", fontSize:10, fontWeight:800, background:C.green, cursor:"pointer" }}>
                              Aceitar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══ PAINEL DIREITO — Dashboard ou Detalhe ═══ */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

          {/* Topbar */}
          <div style={{ padding:"10px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontWeight:800, fontSize:17 }}><span style={{ color:C.red }}>Mix</span><span style={{ color:C.green }}>Up</span></span>
              <button onClick={toggleOnline} style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:16, background:lojaOnline?`${C.green}18`:`${C.red}18`, border:`1px solid ${lojaOnline?C.green+"55":C.red+"55"}`, cursor:"pointer", opacity:togglingOnline?0.5:1 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:lojaOnline?C.greenLight:C.red, boxShadow:lojaOnline?`0 0 6px ${C.greenLight}`:"none" }} />
                <span style={{ fontSize:11, fontWeight:700, color:lojaOnline?C.greenLight:C.red }}>{lojaOnline?"Online":"Offline"}</span>
              </button>
              <button onClick={toggleAutoAceite} style={{ padding:"4px 10px", borderRadius:16, background:autoAceite?"#8B5CF618":C.card, border:`1px solid ${autoAceite?"#8B5CF655":C.border}`, cursor:"pointer" }}>
                <span style={{ fontSize:11, fontWeight:700, color:autoAceite?"#8B5CF6":C.textMuted }}>{autoAceite?"Auto ON":"Auto OFF"}</span>
              </button>
            </div>
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={()=>{ markInteracting(); setShowVendas(true); }} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${C.greenLight}40`, background:`${C.greenLight}12`, color:C.greenLight, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                Vendas {fmt(totalVendas)}
              </button>
              {saldo !== null && (
                <button onClick={()=>{ markInteracting(); setShowSaldo(true); }} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}12`, color:C.blue, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                  Saldo {fmt(saldo)}
                </button>
              )}
            </div>
          </div>

          {/* Conteúdo: Dashboard ou Detalhe */}
          {sel ? (
            /* ═══ DETALHE DO PEDIDO ═══ */
            <div style={{ flex:1, overflowY:"auto", padding:16 }} onScroll={markInteracting}>
              <button onClick={() => setSelected(null)} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.card, color:C.textSoft, fontSize:12, fontWeight:700, cursor:"pointer", marginBottom:14 }}>
                ← Voltar ao painel
              </button>

              <div style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
                {/* Coluna esquerda detalhe */}
                <div style={{ flex:1 }}>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontWeight:900, fontSize:24 }}>#{sel.codigo}</div>
                    <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>{sel.criado_em ? new Date(sel.criado_em).toLocaleString("pt-BR") : ""}</div>
                  </div>

                  {/* Progress bar */}
                  {!["cancelado"].includes(sel.status) && (() => {
                    const pIdx = progressIndex(sel.status);
                    return (
                      <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:18, padding:"8px 0" }}>
                        {PROGRESS_STEPS.map((step, i) => {
                          const active = i <= pIdx;
                          const current = i === pIdx;
                          return (
                            <div key={step.key} style={{ display:"flex", alignItems:"center", flex: i < PROGRESS_STEPS.length-1 ? 1 : "none" }}>
                              <div style={{ width:current?30:24, height:current?30:24, borderRadius:"50%", background:active?C.red:`${C.border}60`,
                                display:"flex", alignItems:"center", justifyContent:"center", fontSize:current?13:10, color:active?"#fff":C.textMuted,
                                fontWeight:800, transition:"all .3s", flexShrink:0, boxShadow:current?`0 0 10px ${C.red}50`:"none" }}>
                                {active ? step.icon : i+1}
                              </div>
                              {i < PROGRESS_STEPS.length-1 && <div style={{ flex:1, height:2, background:i<pIdx?C.red:`${C.border}40`, margin:"0 2px", borderRadius:2 }} />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Status badge */}
                  <span style={{ display:"inline-block", marginBottom:12, fontSize:11, fontWeight:700, color:STATUS[sel.status]?.color, background:STATUS[sel.status]?.bg, padding:"5px 14px", borderRadius:8 }}>{stLabel(sel.status)}</span>

                  {/* Info */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                    <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:9, color:C.textMuted }}>Cliente</div>
                      <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>{sel.cliente_nome || (sel.observacao?.includes("[ENTREGA AVULSA]") ? sel.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim()||"Avulso" : `#${sel.cliente_id}`)}</div>
                    </div>
                    <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:9, color:C.textMuted }}>Telefone</div>
                      <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>{sel.telefone_entrega || sel.observacao?.match(/Tel: ([^•]+)/)?.[1]?.trim() || "—"}</div>
                    </div>
                  </div>
                  <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}`, marginBottom:10 }}>
                    <div style={{ fontSize:9, color:C.textMuted }}>Pagamento</div>
                    <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>{(sel.forma_pagamento||"—").toUpperCase()}</div>
                  </div>
                  <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}`, marginBottom:10 }}>
                    <span style={{ fontSize:9, color:C.textMuted, display:"block", marginBottom:3 }}>Endereço</span>
                    <strong style={{ fontSize:13 }}>📍 {sel.endereco_entrega || "—"}</strong>
                  </div>

                  {/* Badges */}
                  {sel.observacao?.includes("[RETIRADA NA LOJA]") && <div style={{ padding:"8px 10px", borderRadius:8, background:"#8B5CF610", border:"1px solid #8B5CF630", fontSize:11, color:"#8B5CF6", fontWeight:700, marginBottom:6 }}>🏪 RETIRADA NA LOJA</div>}
                  {sel.observacao?.includes("[PAGO ONLINE]") && <div style={{ padding:"8px 10px", borderRadius:8, background:`${C.green}10`, border:`1px solid ${C.green}30`, fontSize:11, color:C.greenLight, fontWeight:700, marginBottom:6 }}>💳 PAGO ONLINE</div>}
                </div>

                {/* Coluna direita detalhe — itens + ações */}
                <div style={{ width:300, flexShrink:0 }}>
                  <div style={{ fontSize:10, fontWeight:800, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Itens</div>
                  {(sel.itens||[]).map((it,idx) => (
                    <div key={idx} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:idx<(sel.itens||[]).length-1?`1px solid ${C.border}40`:"none" }}>
                      <div style={{ width:24, height:24, borderRadius:6, background:`${C.red}15`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:10, color:C.red, flexShrink:0 }}>{it.quantidade}x</div>
                      <span style={{ flex:1, fontSize:12, fontWeight:600 }}>{it.nome}</span>
                      <span style={{ fontWeight:700, fontSize:12 }}>{fmt(it.preco_total)}</span>
                    </div>
                  ))}
                  <div style={{ marginTop:8, paddingTop:8, borderTop:`2px solid ${C.border}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}><span style={{ fontSize:11, color:C.textMuted }}>Subtotal</span><span style={{ fontSize:11, fontWeight:600 }}>{fmt(sel.subtotal)}</span></div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}><span style={{ fontSize:11, color:C.textMuted }}>Taxa</span><span style={{ fontSize:11, fontWeight:600 }}>{fmt(sel.taxa_entrega)}</span></div>
                    <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ fontWeight:800, fontSize:15 }}>Total</span><span style={{ fontWeight:900, fontSize:18, color:C.green }}>{fmt(sel.total)}</span></div>
                  </div>

                  {/* Botões */}
                  <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:16 }}>
                    {!["entregue","cancelado"].includes(sel.status) && (
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={() => abrirChat(sel)} style={{ flex:1, padding:8, borderRadius:8, border:"1px solid #075E5440", background:"#075E5415", color:"#075E54", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                          💬 Cliente {chatNaoLidos[sel.id]>0&&<span style={{ marginLeft:3, background:C.red, color:"#fff", fontSize:9, padding:"0 4px", borderRadius:6 }}>{chatNaoLidos[sel.id]}</span>}
                        </button>
                        {sel.entregador_id && <button onClick={() => abrirChatEnt(sel)} style={{ flex:1, padding:8, borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}15`, color:C.blue, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                          🛵 Entregador {chatNaoLidosEnt[sel.id]>0&&<span style={{ marginLeft:3, background:C.red, color:"#fff", fontSize:9, padding:"0 4px", borderRadius:6 }}>{chatNaoLidosEnt[sel.id]}</span>}
                        </button>}
                      </div>
                    )}
                    <button onClick={() => imprimirPedido(sel)} style={{ padding:8, borderRadius:8, border:`1px solid ${C.border}`, background:C.card, color:C.textSoft, fontSize:11, fontWeight:700, cursor:"pointer" }}>🖨 Imprimir</button>

                    {sel.status === "novo" && <button onClick={() => { markInteracting(); pararSom(); onAdvance(sel.id); setTimeout(()=>onAdvance(sel.id),600); }} style={{ padding:12, borderRadius:10, border:"none", color:"#fff", fontWeight:800, fontSize:14, background:`linear-gradient(135deg,${C.green},${C.greenLight})`, cursor:"pointer" }}>✓ Aceitar Pedido</button>}

                    {/* Fase 2 — Aceito: 2 botões para chamar entregador */}
                    {(sel.status==="aceito"||sel.status==="preparando") && (
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {sel.observacao?.includes("[RETIRADA NA LOJA]") ? (
                          <button onClick={async () => { markInteracting(); try { await put("pedidos", sel.id, { status:"entregue" }); notify("Retirado!"); onRefresh(); } catch { notify("Erro",false); } }}
                            style={{ padding:12, borderRadius:10, border:"none", color:"#fff", fontWeight:800, fontSize:14, background:`linear-gradient(135deg,${C.green},${C.greenLight})`, cursor:"pointer" }}>
                            ✓ Cliente Retirou
                          </button>
                        ) : (<>
                          <button onClick={async () => { markInteracting(); try {
                            // 1. Registrar chamada geral (salva fixo_ids pra exclusão)
                            await fetch("https://mixupbr.com.br/api/loja_entregadores/", {
                              method:"POST", headers:{"Content-Type":"application/json"},
                              body: JSON.stringify({ loja_id: lojaId, pedido_id: sel.id, acao: "notificar_todos" })
                            });
                            // 2. Setar status pronto
                            await put("pedidos", sel.id, { status:"pronto" });
                            notify("Chamando todos os entregadores online!"); onRefresh();
                          } catch { notify("Erro",false); } }}
                            style={{ padding:12, borderRadius:10, border:"none", color:"#fff", fontWeight:800, fontSize:14, background:`linear-gradient(135deg,#ea580c,${C.orange})`, cursor:"pointer" }}>
                            📢 Chamar Entregadores
                          </button>
                          <button onClick={async () => { markInteracting(); try {
                            // 1. Registrar fixos no pedido (salva IDs dos fixos)
                            const resp = await fetch("https://mixupbr.com.br/api/loja_entregadores/", {
                              method:"POST", headers:{"Content-Type":"application/json"},
                              body: JSON.stringify({ loja_id: lojaId, pedido_id: sel.id, acao: "notificar_fixos" })
                            });
                            const data = await resp.json();
                            if (!data.ok && data.erro) { notify(data.erro, false); return; }
                            // 2. Setar status pronto
                            await put("pedidos", sel.id, { status:"pronto" });
                            notify("Chamando " + (data.total||"") + " entregador(es) fixo(s)!"); onRefresh();
                          } catch { notify("Erro",false); } }}
                            style={{ padding:12, borderRadius:10, border:`2px solid ${C.orange}`, color:C.orange, fontWeight:800, fontSize:14, background:"transparent", cursor:"pointer" }}>
                            🔒 Entregador Fixo
                          </button>
                        </>)}
                      </div>
                    )}

                    {/* Fase 3 — Aguardando entregador */}
                    {(sel.status==="pronto"||sel.status==="buscando_entregador") && !sel.observacao?.includes("[RETIRADA NA LOJA]") && (
                      <div style={{ padding:10, borderRadius:8, background:`${C.blue}12`, border:`1px solid ${C.blue}30`, textAlign:"center", fontSize:12, color:C.blue, fontWeight:700 }}>🛵 Aguardando entregador aceitar...</div>
                    )}

                    {/* Fase 4 — Em rota */}
                    {sel.status==="em_entrega" && <div style={{ padding:10, borderRadius:8, background:`${C.green}12`, border:`1px solid ${C.green}30`, textAlign:"center", fontSize:12, color:C.greenLight, fontWeight:700 }}>🛵 Em rota de entrega</div>}

                    {!["entregue","cancelado"].includes(sel.status) && (() => {
                      const prev = { aceito:"novo", preparando:"novo", pronto:"aceito", buscando_entregador:"aceito", em_entrega:"pronto" };
                      return (
                        <div style={{ display:"flex", gap:6, marginTop:2 }}>
                          {prev[sel.status] && <button onClick={async () => { markInteracting(); try { await put("pedidos", sel.id, { status:prev[sel.status] }); notify("Voltou"); onRefresh(); } catch { notify("Erro",false); } }} style={{ flex:1, padding:8, borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}12`, color:C.blue, fontWeight:700, fontSize:11, cursor:"pointer" }}>← Voltar</button>}
                          <button onClick={() => { markInteracting(); if (window.confirm("Cancelar #"+sel.codigo+"?")) { onReject(sel.id); setSelected(null); } }} style={{ flex:1, padding:8, borderRadius:8, border:`1px solid ${C.red}40`, background:`${C.red}12`, color:C.red, fontWeight:700, fontSize:11, cursor:"pointer" }}>✕ Cancelar</button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ═══ DASHBOARD — quando nenhum pedido selecionado ═══ */
            <div style={{ flex:1, overflowY:"auto", padding:16 }}>
              {/* Cards de ação */}
              <div style={{ display:"flex", gap:10, marginBottom:18 }}>
                <div onClick={() => { markInteracting(); setShowAvulsa(true); setAvulsaStep(1); fetchLojaModo(); }}
                  style={{ flex:1, background:`linear-gradient(135deg,${C.green},${C.greenLight})`, borderRadius:14, padding:16, cursor:"pointer", transition:"all .2s" }}>
                  <div style={{ fontSize:28, marginBottom:4 }}>📋</div>
                  <div style={{ color:"#fff", fontWeight:700, fontSize:14 }}>Entrega avulsa</div>
                  <div style={{ color:"rgba(255,255,255,0.7)", fontSize:11, marginTop:2 }}>Cola do WhatsApp · 3 cliques</div>
                </div>
                <div onClick={criarPedidoTeste}
                  style={{ flex:1, background:`${C.orange}15`, borderRadius:14, padding:16, cursor:"pointer", border:`1px solid ${C.orange}40` }}>
                  <div style={{ fontSize:28, marginBottom:4 }}>🧪</div>
                  <div style={{ color:C.orange, fontWeight:700, fontSize:14 }}>Pedido teste</div>
                  <div style={{ color:C.textMuted, fontSize:11, marginTop:2 }}>Criar teste rápido</div>
                </div>
                <div onClick={() => { markInteracting(); setShowVendas(true); }}
                  style={{ flex:1, background:`${C.blue}15`, borderRadius:14, padding:16, cursor:"pointer", border:`1px solid ${C.blue}40` }}>
                  <div style={{ fontSize:28, marginBottom:4 }}>📊</div>
                  <div style={{ color:C.blue, fontWeight:700, fontSize:14 }}>Vendas do dia</div>
                  <div style={{ color:C.textMuted, fontSize:11, marginTop:2 }}>{vendasDone.length} pedidos</div>
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:18 }}>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Total vendido</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.greenLight }}>{fmt(totalVendas)}</div>
                </div>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Pedidos mês</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.text }}>{orders.length}</div>
                </div>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Ticket médio</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.blue }}>{vendasDone.length > 0 ? fmt(totalVendas/vendasDone.length) : "—"}</div>
                </div>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Horário</div>
                  <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{loja?.horario_abre||"08:00"} - {loja?.horario_fecha||"23:00"}</div>
                </div>
              </div>

              {/* Breakdown pagamento */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:18 }}>
                {[{v:vendaPix,l:"PIX",c:C.green},{v:vendaDinheiro,l:"Dinheiro",c:C.yellow},{v:vendaCartao,l:"Cartão",c:C.red},{v:vendaCash,l:"Cashback",c:"#8B5CF6"}].map((x,i) => (
                  <div key={i} style={{ background:C.card, borderRadius:10, padding:12, border:`1px solid ${C.border}`, textAlign:"center" }}>
                    <div style={{ fontSize:16, fontWeight:800, color:x.c }}>{fmt(x.v)}</div>
                    <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>{x.l}</div>
                  </div>
                ))}
              </div>

              {/* Últimos entregues */}
              <div style={{ fontSize:11, fontWeight:800, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Últimos entregues</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                {orders.filter(o => o.status === "entregue").slice(0, 6).map(o => (
                  <div key={o.id} onClick={() => { markInteracting(); setSelected(o); }}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", borderRadius:10,
                      background:C.card, border:`1px solid ${C.border}`, cursor:"pointer", fontSize:12 }}>
                    <div>
                      <span style={{ color:C.textMuted }}>#{o.codigo}</span>
                      <div style={{ fontSize:10, color:C.textMuted, marginTop:2 }}>{o.cliente_nome || `#${o.cliente_id}`}</div>
                    </div>
                    <span style={{ fontWeight:700, color:C.greenLight }}>{fmt(o.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ MODALS CHAT ═══ */}
      {showChatEnt && (
        <div onClick={fecharChatEnt} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:2000, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:500, height:"80vh", background:C.card, borderTopLeftRadius:24, borderTopRightRadius:24, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ background:"#1a1a2e", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div><div style={{ color:"#fff", fontWeight:800, fontSize:16 }}>🛵 Pedido #{chatEntPedido?.codigo||chatEntPedido?.id}</div><div style={{ color:"rgba(255,255,255,0.7)", fontSize:12 }}>Entregador #{chatEntPedido?.entregador_id}</div></div>
              <button onClick={fecharChatEnt} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:20, width:36, height:36, cursor:"pointer", fontSize:18, fontWeight:700 }}>✕</button>
            </div>
            <div ref={chatEntScrollRef} style={{ flex:1, overflowY:"auto", padding:"12px 16px", background:"#ECE5DD", display:"flex", flexDirection:"column", gap:6 }}>
              {chatEntLoading ? <div style={{ textAlign:"center", marginTop:40, color:"#666" }}>Carregando...</div>
              : chatEntMsgs.length===0 ? <div style={{ textAlign:"center", marginTop:60, color:"#999" }}><div style={{ fontSize:36, marginBottom:8 }}>🛵</div><div>Nenhuma mensagem</div></div>
              : chatEntMsgs.map((m,i) => { const eu = m.remetente_tipo==="loja"; return (
                <div key={m.id||i} style={{ display:"flex", justifyContent:eu?"flex-end":"flex-start" }}>
                  <div style={{ maxWidth:"78%", padding:"8px 12px", borderRadius:16, borderTopRightRadius:eu?4:16, borderTopLeftRadius:eu?16:4, background:eu?"#DCF8C6":"#fff", boxShadow:"0 1px 2px rgba(0,0,0,0.08)" }}>
                    {!eu && <div style={{ fontSize:11, fontWeight:700, color:"#1a1a2e", marginBottom:3 }}>{m.remetente_nome}</div>}
                    <div style={{ fontSize:14, color:"#111", lineHeight:1.4 }}>{m.mensagem}</div>
                    <div style={{ fontSize:10, color:"#999", textAlign:"right", marginTop:3 }}>{new Date(m.criado_em).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}{eu&&<span style={{ marginLeft:4, color:m.lido?"#34B7F1":"#aaa" }}>{m.lido?"✓✓":"✓"}</span>}</div>
                  </div>
                </div>); })}
            </div>
            <div style={{ display:"flex", gap:10, padding:"12px 14px", background:C.card, borderTop:`1px solid ${C.border}`, alignItems:"flex-end" }}>
              <input value={chatEntTexto} onChange={e=>setChatEntTexto(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),enviarMsgChatEnt())} placeholder="Mensagem..." style={{ flex:1, padding:"10px 14px", borderRadius:24, border:`1px solid ${C.border}`, fontSize:14, outline:"none", background:C.bg, color:C.text }} />
              <button onClick={enviarMsgChatEnt} disabled={!chatEntTexto.trim()} style={{ width:44, height:44, borderRadius:22, border:"none", background:chatEntTexto.trim()?"#1a1a2e":C.border, color:"#fff", fontSize:18, cursor:"pointer", flexShrink:0 }}>➤</button>
            </div>
          </div>
        </div>
      )}

      {showChat && (
        <div onClick={fecharChat} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:2000, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:500, height:"80vh", background:C.card, borderTopLeftRadius:24, borderTopRightRadius:24, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ background:"#075E54", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div><div style={{ color:"#fff", fontWeight:800, fontSize:16 }}>Chat — Pedido #{chatPedido?.codigo||chatPedido?.id}</div><div style={{ color:"rgba(255,255,255,0.7)", fontSize:12 }}>Cliente #{chatPedido?.cliente_id}</div></div>
              <button onClick={fecharChat} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:20, width:36, height:36, cursor:"pointer", fontSize:18, fontWeight:700 }}>✕</button>
            </div>
            <div ref={chatScrollRef} style={{ flex:1, overflowY:"auto", padding:"12px 16px", background:"#ECE5DD", display:"flex", flexDirection:"column", gap:6 }}>
              {chatLoading ? <div style={{ textAlign:"center", marginTop:40, color:"#666" }}>Carregando...</div>
              : chatMsgs.length===0 ? <div style={{ textAlign:"center", marginTop:60, color:"#999" }}><div style={{ fontSize:36, marginBottom:8 }}>💬</div><div>Nenhuma mensagem</div></div>
              : chatMsgs.map((m,i) => { const eu = m.remetente_tipo==="loja"; return (
                <div key={m.id||i} style={{ display:"flex", justifyContent:eu?"flex-end":"flex-start" }}>
                  <div style={{ maxWidth:"78%", padding:"8px 12px", borderRadius:16, borderTopRightRadius:eu?4:16, borderTopLeftRadius:eu?16:4, background:eu?"#DCF8C6":"#fff", boxShadow:"0 1px 2px rgba(0,0,0,0.08)" }}>
                    {!eu && <div style={{ fontSize:11, fontWeight:700, color:"#075E54", marginBottom:3 }}>{m.remetente_nome}</div>}
                    <div style={{ fontSize:14, color:"#111", lineHeight:1.4 }}>{m.mensagem}</div>
                    <div style={{ fontSize:10, color:"#999", textAlign:"right", marginTop:3 }}>{new Date(m.criado_em).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}{eu&&<span style={{ marginLeft:4, color:m.lido?"#34B7F1":"#aaa" }}>{m.lido?"✓✓":"✓"}</span>}</div>
                  </div>
                </div>); })}
            </div>
            <div style={{ display:"flex", gap:10, padding:"12px 14px", background:C.card, borderTop:`1px solid ${C.border}`, alignItems:"flex-end" }}>
              <input value={chatTexto} onChange={e=>setChatTexto(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),enviarMsgChat())} placeholder="Mensagem..." style={{ flex:1, padding:"10px 14px", borderRadius:24, border:`1px solid ${C.border}`, fontSize:14, outline:"none", background:C.bg, color:C.text }} />
              <button onClick={enviarMsgChat} disabled={!chatTexto.trim()} style={{ width:44, height:44, borderRadius:22, border:"none", background:chatTexto.trim()?"#075E54":C.border, color:"#fff", fontSize:18, cursor:"pointer", flexShrink:0 }}>➤</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
